import { StateCreator } from 'zustand';
import { v4 as uuidv4 } from 'uuid';
import { deepCopy, getApiErrorMessage } from 'src/utils/CommonUtils';
import { cloneTaskDefinition } from 'src/components/createWorklow/createTaskDefinitionUtils';
import { FlashbarProps } from '@amzn/awsui-components-react-v3';

import { logger } from 'src/utils/logger';
import { Store } from 'src/stores/store.interface';
import {
	getGraphValidationErrors,
	getLayoutedElements,
	isStartOrEndNode,
	scrollWorkflowDesginer,
} from 'src/components/createWorklow/updateWFDefinitionCanvas/updateWFDefinitionCanvasUtils';
import { validateTaskDefinition } from 'src/components/createWorklow/updateTaskDefinition/updateTaskDefinitionUtils';
import { isEmptyAttributeValue } from 'src/utils/attributeTypeUtils';
import { SUB_WORKFLOW_TASK_TYPE } from 'src/constants/CommonConstants';
import { GraphLayoutDirection } from 'src/constants/selfServe/executionViewConstants';
import {
	CompleteWorkflowDefinition,
	Dependancy,
	TaskDefinition,
	WorkflowDefinitionCanvasStore,
} from './workflowDefinitionCanvasStore.interface';
import { callSNOWDataService } from 'src/utils/snowDataServiceWrapper';
import {
	ReactFlowHelpNodeIDList,
	ReactFlowNodeStatus,
} from 'src/components/createWorklow/customNode/customNodeEnums';
import { SNOW_NXT_WORKFLOW_ENDPOINT } from 'src/config';
import { ApiStatus } from 'src/constants/commonEnums';
import { WorkflowIntention } from '@amzn/snow-nxt-workflow-service-typescript-client';
import { canvasHistoryManager } from 'src/components/createWorklow/canvasActionButtons/undoRedoHistory';

const FIT_VIEW_PADDING = 0.2;
const FIT_VIEW_DURATION = 300;
const FIT_VIEW_DELAY_MS = 50;

const applyLayoutWithDirection = (
	get: () => Store,
	direction: GraphLayoutDirection
) => {
	const reactFlowInstance = get().reactFlowInstance;
	const nodes = reactFlowInstance.getNodes();
	const edges = reactFlowInstance.getEdges();
	const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
		nodes,
		edges,
		direction
	);

	reactFlowInstance.setNodes(layoutedNodes);
	reactFlowInstance.setEdges(layoutedEdges);

	setTimeout(() => {
		reactFlowInstance.fitView({
			padding: FIT_VIEW_PADDING,
			duration: FIT_VIEW_DURATION,
		});
	}, FIT_VIEW_DELAY_MS);
};

const defaultWFDefinition = {
	dependencies: [],
	taskDefinitions: [],
};

export const defaultWorkflowDefinitionCanvasStore = {
	reactFlowInstance: {},
	isWFDefinitionLoading: false,
	WFDefinition: defaultWFDefinition,
	WFDefinitionError: '',
	isWFDefinitionUpdating: false,
	childWorkflowDefinitions: {},
	flashBarItems: [],
	canDelete: false,
	activeView: 'canvas' as const,
	...canvasHistoryManager.getInitialState(),
};

const workflowDefinitionCanvasStore: StateCreator<
	Store,
	[],
	[],
	WorkflowDefinitionCanvasStore
> = (set, get) => ({
	...defaultWorkflowDefinitionCanvasStore,
	setActiveView: (view) => {
		set({ activeView: view });
	},
	setWFDefinition: (wfDefinition) => {
		set({ WFDefinition: wfDefinition });
	},
	fetchWFDefinition: async (workflowId) => {
		try {
			set({ isWFDefinitionLoading: true });
			const apiResponse = await callSNOWDataService({
				path: `/workflows/${workflowId}`,
				method: 'GET',
				url: SNOW_NXT_WORKFLOW_ENDPOINT,
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} as any);

			set({
				isWFDefinitionLoading: false,
				WFDefinition: apiResponse,
				workflowCreationMetaData: {
					...apiResponse.workflowDefinition,
					supportedTasks: [''],
				},
			});

			logger.info(
				`Response from read workflowDefiniton api [ workflowId :  workflowDefinition ] ::: [ ${workflowId} : ${JSON.stringify(
					apiResponse
				)} ]`
			);
		} catch (error) {
			logger.error(
				`Exception occured while calling read workflowDefiniton api for request [ workflowId : Error ] ::: [ ${workflowId} : ${JSON.stringify(
					error
				)} ]`
			);
			set({
				isWFDefinitionLoading: false,
				WFDefinitionError: getApiErrorMessage(error),
			});
		}
	},

	fetchChildWorkflowDefinition: async (childWorkflowId) => {
		const cachedChildWorkflowDefinition =
			get().childWorkflowDefinitions[childWorkflowId];

		if (cachedChildWorkflowDefinition) {
			return cachedChildWorkflowDefinition;
		}

		try {
			const childWorkflowDefinition = (await callSNOWDataService({
				path: `/workflows/${childWorkflowId}`,
				method: 'GET',
				url: SNOW_NXT_WORKFLOW_ENDPOINT,
				body: null,
			})) as CompleteWorkflowDefinition;

			set({
				childWorkflowDefinitions: {
					...get().childWorkflowDefinitions,
					[childWorkflowId]: childWorkflowDefinition,
				},
			});

			return childWorkflowDefinition;
		} catch (error) {
			logger.error(
				`Exception occured while reading child workflowDefinition [ childWorkflowId : Error ] ::: [ ${childWorkflowId} : ${JSON.stringify(
					error
				)} ]`
			);
			get().setFlashBarItems([
				`Unable to read the sub-workflow ${childWorkflowId}. ${getApiErrorMessage(
					error
				)}`,
			]);

			return null;
		}
	},

	updateReactFlowNodeDetails: (
		nodeId,
		nodeAdditionalPropsUpdates,
		taskDefinitionUpdates
	) => {
		const nodes = get().reactFlowInstance.getNodes();
		const nodeIndex = nodes.findIndex((node) => node.id === nodeId);

		const newNode = deepCopy(nodes[nodeIndex]);

		if (nodeIndex < 0) {
			get().setFlashBarItems(['Something  went Wrong']);

			return;
		}

		newNode.data.taskDefinition = {
			...nodes[nodeIndex].data.taskDefinition,
			...taskDefinitionUpdates,
		};
		newNode.data.nodeAdditionalProps = {
			...nodes[nodeIndex].data.nodeAdditionalProps,
			...nodeAdditionalPropsUpdates,
		};
		nodes[nodeIndex] = newNode;
		get().reactFlowInstance.setNodes([...nodes]);
	},

	updateWFDefinition: async (WFDefinition, workflowId, currentUserName) => {
		try {
			set({ isWFDefinitionUpdating: true });

			const { workflowDefinitionName, version, entityType } =
				WFDefinition.workflowDefinition;

			// Propagate workflow-level entityType to all task definitions' entityTypeSupported
			const updatedTaskDefinitions = WFDefinition.taskDefinitions.map(
				(task) => ({
					...task,
					entityTypeSupported: entityType || task.entityTypeSupported,
					...(task.taskType === SUB_WORKFLOW_TASK_TYPE && {
						taskInputs: (task.taskInputs ?? []).filter(
							(taskInput) => !isEmptyAttributeValue(taskInput.value)
						),
					}),
				})
			);

			const body = {
				...WFDefinition,
				taskDefinitions: updatedTaskDefinitions,
				workflowDefinition: {
					...WFDefinition.workflowDefinition,
					lastUpdatedBy: currentUserName,
				},
				requestId: uuidv4(),
			};

			const apiResponse = await callSNOWDataService({
				path: `/workflows/${workflowId}`,
				method: 'PUT',
				body,
				url: SNOW_NXT_WORKFLOW_ENDPOINT,
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} as any);

			set({
				workflowCreationMetaData: {
					...WFDefinition.workflowDefinition,
					version: version + 1,
				},
				isWFDefinitionUpdating: false,
				flashBarItems: [
					{
						content: `${workflowDefinitionName} workflow has been successfully saved as DRAFT`,
						type: 'success',
						dismissible: true,
						onDismiss: () => get().setFlashBarItems([]),
					},
				],
			});

			logger.info(
				`Response from updateWorkflowDefiniton api [ workflowId : WFDefinition ] ::: [ ${workflowId} : ${JSON.stringify(
					apiResponse
				)} ]`
			);

			return ApiStatus.SUCCESS;
		} catch (error) {
			logger.error(
				`Exception occured while calling updateWorkflowDefiniton api for request [ workflowId : updateWFDefintion :Error ] ::: [ ${workflowId} :  ${WFDefinition} : ${JSON.stringify(
					error
				)} ]`
			);
			set({
				isWFDefinitionUpdating: false,
				flashBarItems: [
					{
						content: getApiErrorMessage(error) as string,
						type: 'error' as FlashbarProps.Type,
						dismissible: true,
						onDismiss: () => get().setFlashBarItems([]),
					},
				],
			});

			return ApiStatus.ERROR;
		} finally {
			set({ isModalVisibleForCreateWF: false });
		}
	},
	updateWFDefinitionAttributes: async (attributes, workflowId) => {
		try {
			set({ isWFDefinitionUpdating: true });

			await callSNOWDataService({
				path: `/workflows/${workflowId}`,
				method: 'PATCH',
				body: { workflowDefinitionAttributes: attributes },
				url: SNOW_NXT_WORKFLOW_ENDPOINT,
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} as any);

			// lastUpdateTime is typed as Date on the vended model but as number on
			// WorkflowCreationMetaData; it is server-owned, so exclude it from the spread.
			const { lastUpdateTime: _lastUpdateTime, ...attributesForMetaData } = attributes;

			set({
				workflowCreationMetaData: {
					...get().workflowCreationMetaData,
					...attributesForMetaData,
					workflowId,
					workflowIntention: attributes.workflowIntention as WorkflowIntention,
				},
				isWFDefinitionUpdating: false,
				flashBarItems: [
					{
						content: `${attributes.workflowDefinitionName} workflow metadata has been successfully saved`,
						type: 'success',
						dismissible: true,
						onDismiss: () => get().setFlashBarItems([]),
					},
				],
			});

			logger.info(
				`Response from updateWorkflowDefinitionAttributes api [ workflowId ] ::: [ ${workflowId} ]`
			);

			return ApiStatus.SUCCESS;
		} catch (error) {
			logger.error(
				`Exception occured while calling updateWorkflowDefinitionAttributes api for workflowId: ${workflowId} : ${JSON.stringify(
					error
				)}`
			);
			set({
				isWFDefinitionUpdating: false,
				flashBarItems: [
					{
						content: getApiErrorMessage(error) as string,
						type: 'error' as FlashbarProps.Type,
						dismissible: true,
						onDismiss: () => get().setFlashBarItems([]),
					},
				],
			});

			return ApiStatus.ERROR;
		} finally {
			set({ isModalVisibleForCreateWF: false });
		}
	},
	setFlashBarItems: (errors, header) => {
		const flashBarItems = errors.map((error) => ({
			header,
			content: error,
			type: 'error' as FlashbarProps.Type,
			dismissible: true,
			onDismiss: () => get().setFlashBarItems([]),
		}));

		set({ flashBarItems });
	},
	updateReactFlowInstance: (reactFlowInstance) => {
		set({ reactFlowInstance });
	},
	submitWorkflowDefinitionCanvasStep: () => {
		const reactFlowInstance = get().reactFlowInstance;
		const workflowCreationMetaData = get().workflowCreationMetaData;
		const nodes = reactFlowInstance?.getNodes
			? reactFlowInstance.getNodes()
			: [];
		const edges = reactFlowInstance?.getEdges
			? reactFlowInstance.getEdges()
			: [];

		const newWFDefinition: CompleteWorkflowDefinition = {
			taskDefinitions: [],
			workflowDefinition: workflowCreationMetaData,
			dependencies: [],
		};

		const errors: string[] = [];

		scrollWorkflowDesginer(-200);

		if (get().activeView === 'table') {
			const { taskDefinitions, dependencies } = get().WFDefinition;

			if (taskDefinitions.length > 0) {
				taskDefinitions.forEach((taskDefinition) => {
					newWFDefinition.taskDefinitions.push(taskDefinition);
					const validationErrors = validateTaskDefinition(taskDefinition);

					errors.push(...validationErrors);

					const hasIncoming = dependencies.some(
						(d) => d.end === taskDefinition.taskId
					);
					const hasOutgoing = dependencies.some(
						(d) => d.start === taskDefinition.taskId
					);

					if (!hasIncoming && !hasOutgoing) {
						errors.push(
							`${taskDefinition.taskName} is disconnected from the workflow`
						);
					} else if (!hasIncoming) {
						errors.push(
							`${taskDefinition.taskName} has no incoming dependency (not reachable from START)`
						);
					} else if (!hasOutgoing) {
						errors.push(
							`${taskDefinition.taskName} has no outgoing dependency (does not lead to END)`
						);
					}
				});

				if (errors.length) {
					get().setFlashBarItems(errors);

					return errors;
				}

				newWFDefinition.dependencies = [...dependencies];

				set({
					WFDefinition: {
						...get().WFDefinition,
						dependencies: newWFDefinition.dependencies,
						taskDefinitions: newWFDefinition.taskDefinitions,
					},
				});
				get().setIsModalVisibleForCreateWF(true);

				return errors;
			}

			errors.push('Add atleast one task in the workflow');
			get().setFlashBarItems(errors);

			return errors;
		}

		nodes.forEach((node) => {
			if (!isStartOrEndNode(node)) {
				const taskDefinition = node.data.taskDefinition;

				newWFDefinition.taskDefinitions.push(taskDefinition);

				const validationErrors = validateTaskDefinition(taskDefinition);

				errors.push(...validationErrors);
				if (errors.length) {
					get().setFlashBarItems(errors);
					get().updateReactFlowNodeDetails(node.id, {
						status: ReactFlowNodeStatus.CREATION_ERROR,
					});

					return errors;
				}
			}
		});

		const { errorMessages, errorNodeIDList } = getGraphValidationErrors(
			reactFlowInstance.getNode(ReactFlowHelpNodeIDList.START),
			nodes,
			edges
		);

		errors.push(...errorMessages);

		errorNodeIDList.forEach((nodeId) => {
			get().updateReactFlowNodeDetails(nodeId, {
				status: ReactFlowNodeStatus.CREATION_ERROR,
			});
		});

		if (errors.length) {
			get().setFlashBarItems(errors);

			return errors;
		}

		edges.forEach(({ source, target }) => {
			const sourceNode = reactFlowInstance.getNode(source);
			const targetNode = reactFlowInstance.getNode(target);

			if (sourceNode && targetNode) {
				newWFDefinition.dependencies.push({
					start: sourceNode.data.taskDefinition.taskId,
					end: targetNode.data.taskDefinition.taskId,
				});
			}
		});
		set({
			WFDefinition: {
				...get().WFDefinition,
				dependencies: newWFDefinition.dependencies,
				taskDefinitions: newWFDefinition.taskDefinitions,
			},
		});
		get().setIsModalVisibleForCreateWF(true);

		return errors;
	},

	autoLayout: () => {
		const reactFlowInstance = get().reactFlowInstance;
		const nodes = reactFlowInstance.getNodes();
		const edges = reactFlowInstance.getEdges();
		const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
			nodes,
			edges,
			GraphLayoutDirection.TopToBottom
		);

		reactFlowInstance.setNodes(layoutedNodes);
		reactFlowInstance.setEdges(layoutedEdges);
	},

	autoLayoutVertical: () => {
		applyLayoutWithDirection(get, GraphLayoutDirection.TopToBottom);
	},

	autoLayoutHorizontal: () => {
		applyLayoutWithDirection(get, GraphLayoutDirection.LeftToRight);
	},

	deleteTask: () => {
		const { reactFlowInstance, activeReactFlowNodeId } = get();
		const selectedNodes = reactFlowInstance
			.getNodes()
			.filter((n) => n.selected);
		const selectedEdges = reactFlowInstance
			.getEdges()
			.filter((e) => e.selected);
		const deletableNodes = selectedNodes.filter((n) => !isStartOrEndNode(n));

		if (deletableNodes.length || selectedEdges.length) {
			reactFlowInstance.deleteElements({
				nodes: deletableNodes,
				edges: selectedEdges,
			});
			setTimeout(() => get().saveToHistory(), 0);
		} else if (activeReactFlowNodeId) {
			const activeNode = reactFlowInstance
				.getNodes()
				.find((n) => n.id === activeReactFlowNodeId);

			if (activeNode && !isStartOrEndNode(activeNode)) {
				reactFlowInstance.deleteElements({
					nodes: [{ id: activeReactFlowNodeId }],
				});
				setTimeout(() => get().saveToHistory(), 0);
			}
		}
	},

	updateCanDelete: () => {
		const { reactFlowInstance } = get();

		if (!reactFlowInstance.getNodes) {
			set({ canDelete: false });

			return;
		}

		const selectedNodes = reactFlowInstance
			.getNodes()
			.filter((n) => n.selected);
		const selectedEdges = reactFlowInstance
			.getEdges()
			.filter((e) => e.selected);
		const hasDeletableNodes = selectedNodes.some((n) => !isStartOrEndNode(n));

		set({ canDelete: hasDeletableNodes || selectedEdges.length > 0 });
	},

	saveToHistory: () => {
		const { reactFlowInstance, history, historyIndex, canUndo, canRedo } =
			get();

		if (!reactFlowInstance.getNodes) return;

		const newHistory = canvasHistoryManager.saveToHistory(
			{
				nodes: reactFlowInstance.getNodes(),
				edges: reactFlowInstance.getEdges(),
			},
			{ history, historyIndex, canUndo, canRedo }
		);

		set(newHistory);
	},

	undo: () => {
		const { reactFlowInstance, history, historyIndex, canUndo, canRedo } =
			get();

		if (!reactFlowInstance.setNodes) return;

		const { newHistory, state } = canvasHistoryManager.undo({
			history,
			historyIndex,
			canUndo,
			canRedo,
		});

		if (state) {
			reactFlowInstance.setNodes(state.nodes);
			reactFlowInstance.setEdges(state.edges);
		}

		set(newHistory);
	},

	redo: () => {
		const { reactFlowInstance, history, historyIndex, canUndo, canRedo } =
			get();

		if (!reactFlowInstance.setNodes) return;

		const { newHistory, state } = canvasHistoryManager.redo({
			history,
			historyIndex,
			canUndo,
			canRedo,
		});

		if (state) {
			reactFlowInstance.setNodes(state.nodes);
			reactFlowInstance.setEdges(state.edges);
		}

		set(newHistory);
	},

	clearHistory: () => set(canvasHistoryManager.clearHistory()),

	duplicateTask: (nodeId: string, userName: string) => {
		const { reactFlowInstance, setActiveTaskDefinitionStore } = get();
		const nodes = reactFlowInstance?.getNodes();

		if (!nodes) return null;
		const sourceNode = nodes.find((n) => n.id === nodeId);

		if (!sourceNode) return null;

		const newNodeId = uuidv4();
		const clonedNode = deepCopy(sourceNode);
		const sourceTaskDef = clonedNode.data.taskDefinition;
		const clonedTaskDef = cloneTaskDefinition(sourceTaskDef, userName);

		const existingCopies = nodes.filter(
			(n) =>
				n.data?.taskDefinition?.taskName?.startsWith(sourceTaskDef.taskName) &&
				n.id !== sourceNode.id
		).length;
		const offset = (existingCopies + 1) * 50;

		const duplicatedNode = {
			...clonedNode,
			id: newNodeId,
			position: {
				x: sourceNode.position.x + offset,
				y: sourceNode.position.y + offset,
			},
			selected: false,
			data: {
				...clonedNode.data,
				taskDefinition: clonedTaskDef,
				nodeAdditionalProps: {
					...clonedNode.data.nodeAdditionalProps,
					name: clonedTaskDef.taskName,
					status: ReactFlowNodeStatus.WORKFLOW_REVIEW,
				},
			},
		};

		reactFlowInstance.setNodes([...nodes, duplicatedNode]);

		setTimeout(() => {
			get().saveToHistory();
			setActiveTaskDefinitionStore(
				{ ...duplicatedNode.data.taskDefinition },
				newNodeId,
				false
			);
		}, 0);

		return newNodeId;
	},

	addTaskDefinition: (taskDefinition) => {
		const { WFDefinition } = get();

		set({
			WFDefinition: {
				...WFDefinition,
				taskDefinitions: [...WFDefinition.taskDefinitions, taskDefinition],
			},
		});
		get().saveToHistory();
	},

	deleteTaskById: (taskId: string) => {
		const { WFDefinition } = get();

		set({
			WFDefinition: {
				...WFDefinition,
				taskDefinitions: WFDefinition.taskDefinitions.filter(
					(t) => t.taskId !== taskId
				),
				dependencies: WFDefinition.dependencies.filter(
					(d) => d.start !== taskId && d.end !== taskId
				),
			},
		});
		get().saveToHistory();
	},

	duplicateTaskById: (taskId: string, userName: string) => {
		const { WFDefinition } = get();
		const sourceTask = WFDefinition.taskDefinitions.find(
			(t) => t.taskId === taskId
		);

		if (!sourceTask) return null;

		const duplicatedTask = cloneTaskDefinition(sourceTask, userName);

		set({
			WFDefinition: {
				...WFDefinition,
				taskDefinitions: [...WFDefinition.taskDefinitions, duplicatedTask],
			},
		});
		get().saveToHistory();

		return duplicatedTask.taskId;
	},

	syncCanvasToStore: () => {
		const { reactFlowInstance } = get();

		if (!reactFlowInstance?.getNodes) return;

		const nodes = reactFlowInstance.getNodes();
		const edges = reactFlowInstance.getEdges();

		const taskDefinitions: TaskDefinition[] = [];
		const dependencies: Dependancy[] = [];

		nodes.forEach((node) => {
			if (!isStartOrEndNode(node) && node.data?.taskDefinition) {
				taskDefinitions.push(node.data.taskDefinition);
			}
		});

		edges.forEach(({ source, target }) => {
			const sourceNode = reactFlowInstance.getNode(source);
			const targetNode = reactFlowInstance.getNode(target);

			if (
				sourceNode?.data?.taskDefinition &&
				targetNode?.data?.taskDefinition
			) {
				dependencies.push({
					start: sourceNode.data.taskDefinition.taskId,
					end: targetNode.data.taskDefinition.taskId,
				});
			}
		});

		set({
			WFDefinition: { ...get().WFDefinition, taskDefinitions, dependencies },
		});
	},
});

export default workflowDefinitionCanvasStore;
